/**
 * VisitTimelineEntry — single entry in a place's visit history.
 * Per UX §3.5: left rail with vertical hairline + accent dot, right side
 * with date in tabular figures, rating dots, notes (clamped to 2 lines by
 * default — "Show more" toggles). Includes edit/delete affordances.
 *
 * Used inside the Place detail timeline and the Home recent-visits feed.
 */
import * as React from 'react';
import { Link } from 'react-router-dom';
import { DotRating } from './ui/DotRating';
import { formatTimelineDate, formatRelative } from '../lib/date';
import { cn } from '../lib/cn';

/**
 * Minimal shape this row needs. Accepts both the full `Visit` domain type
 * AND the joined `VisitFeedItem` shape used by the household-wide feed.
 */
export interface TimelineItem {
  id: string;
  name: string;
  rating: 1 | 2 | 3 | 4 | 5 | null;
  notes: string | null;
}

export interface TimelineVisit {
  id: string;
  visited_at: string;
  rating: 1 | 2 | 3 | 4 | 5;
  notes: string | null;
  /** Optional dishes / rooms / work performed during the visit. */
  items?: TimelineItem[];
}

export interface VisitTimelineEntryProps {
  visit: TimelineVisit;
  /** Show the place name on top (used in Home feed where there's no place
   * context). */
  placeName?: string | null;
  /** When defined, the place name links to its detail page. */
  placeId?: string | null;
  onEdit?: () => void;
  onDelete?: () => void;
  /** Date rendering: 'full' (Mar 14, 2026) or 'relative' (Yesterday). */
  dateFormat?: 'full' | 'relative';
  className?: string;
}

export function VisitTimelineEntry({
  visit,
  placeName,
  placeId,
  onEdit,
  onDelete,
  dateFormat = 'full',
  className,
}: VisitTimelineEntryProps) {
  const [expanded, setExpanded] = React.useState(false);
  const notes = visit.notes ?? '';
  const isClamped = !expanded && notes.length > 240;
  const displayNotes = isClamped ? `${notes.slice(0, 240).trim()}…` : notes;
  const dateLabel =
    dateFormat === 'relative'
      ? formatRelative(visit.visited_at)
      : formatTimelineDate(visit.visited_at);

  return (
    <article
      role="article"
      aria-label={`Visit on ${formatTimelineDate(visit.visited_at)}, rated ${visit.rating} of 5 dots`}
      className={cn(
        'group relative pl-6 py-6 border-b border-ink-100 last:border-b-0',
        className,
      )}
      data-testid="visit-timeline-entry"
    >
      <span
        aria-hidden
        className="absolute left-0 top-8 h-1.5 w-1.5 rounded-full bg-accent"
      />
      <span
        aria-hidden
        className="absolute left-[3px] top-0 bottom-0 w-px bg-ink-200"
      />
      <header className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          {placeName ? (
            <p className="font-sans text-[0.65rem] uppercase tracking-[0.18em] text-ink-500">
              {placeId ? (
                <Link
                  to={`/places/${placeId}`}
                  className="hover:text-ink-900 focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
                >
                  {placeName}
                </Link>
              ) : (
                placeName
              )}
            </p>
          ) : null}
          <p className="mt-0.5 font-sans text-sm text-ink-700 tabular-nums">
            {dateLabel}
          </p>
        </div>
        <DotRating value={visit.rating} size="sm" readOnly />
      </header>

      {notes ? (
        <p
          className={cn(
            'mt-4 font-serif text-base text-ink-900 leading-relaxed',
            'whitespace-pre-wrap',
          )}
        >
          {displayNotes}
          {isClamped ? (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="ml-2 text-xs uppercase tracking-widest text-ink-500 hover:text-ink-900 focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
            >
              Show more
            </button>
          ) : null}
        </p>
      ) : (
        <p className="mt-4 text-xs italic text-ink-500">No notes for this entry.</p>
      )}

      {visit.items && visit.items.length > 0 ? (
        <ul className="mt-4 space-y-2 border-l border-ink-200 pl-4">
          {visit.items.map((it) => (
            <li
              key={it.id}
              className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm"
            >
              <span className="font-serif text-ink-900">{it.name}</span>
              {it.rating !== null ? (
                <DotRating value={it.rating} size="sm" readOnly />
              ) : null}
              {it.notes ? (
                <span className="font-serif italic text-ink-500">
                  — {it.notes}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {onEdit || onDelete ? (
        <div className="mt-4 flex items-center gap-4 text-xs text-ink-500">
          {onEdit ? (
            <button
              type="button"
              onClick={onEdit}
              className="hover:text-ink-900 uppercase tracking-widest text-[0.65rem] focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
            >
              Edit
            </button>
          ) : null}
          {onDelete ? (
            <button
              type="button"
              onClick={onDelete}
              className="hover:text-error-muted uppercase tracking-widest text-[0.65rem] focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
            >
              Delete
            </button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
